import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { KpiDenominatorHttpService } from '@services/datasources/kpi-denominator-http.service';
import { EntityKpiDenominatorDto } from 'app/@types/dto/entity-kpi-denominator-dto.model';
import { OrganizationKpiDenominatorTypeDto } from 'app/@types/dto/organization-kpi-denominator-type-dto.model';

export interface KpiDenominatorStateModel {
    organizationId: string | undefined;
    types: OrganizationKpiDenominatorTypeDto[];
    typesLoaded: boolean;
    denominators: EntityKpiDenominatorDto[];
    loading: boolean;
    error: string | undefined;
}

const denominatorKey = (d: EntityKpiDenominatorDto): string =>
    `${d.organizationEntityId}|${d.refYear}|${d.kpiDenominatorTypeId}`;

const sortTypes = (types: OrganizationKpiDenominatorTypeDto[]): OrganizationKpiDenominatorTypeDto[] =>
    [...types].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

// eslint-disable-next-line @typescript-eslint/no-namespace -- NGXS action grouping requires namespace for combined value+type semantics
export namespace KpiDenominatorActions {
    export class LoadTypes {
        static readonly type = '[KpiDenominator] Load Types';
        constructor(public orgId: string) {}
    }
    export class CreateType {
        static readonly type = '[KpiDenominator] Create Type';
        constructor(
            public orgId: string,
            public kpiType: OrganizationKpiDenominatorTypeDto,
            public completion?: Subject<OrganizationKpiDenominatorTypeDto | undefined>,
        ) {}
    }
    export class UpdateType {
        static readonly type = '[KpiDenominator] Update Type';
        constructor(
            public orgId: string,
            public typeId: string,
            public kpiType: OrganizationKpiDenominatorTypeDto,
            public completion?: Subject<OrganizationKpiDenominatorTypeDto | undefined>,
        ) {}
    }
    export class DeleteType {
        static readonly type = '[KpiDenominator] Delete Type';
        constructor(
            public orgId: string,
            public typeId: string,
            public completion?: Subject<boolean>,
        ) {}
    }
    export class LoadDenominators {
        static readonly type = '[KpiDenominator] Load Denominators';
        constructor(
            public orgId: string,
            public years: number[],
        ) {}
    }
    export class UpsertDenominators {
        static readonly type = '[KpiDenominator] Upsert Denominators';
        constructor(
            public orgId: string,
            public values: EntityKpiDenominatorDto[],
            public completion?: Subject<boolean>,
        ) {}
    }
    export class ClearState {
        static readonly type = '[KpiDenominator] Clear State';
    }
}

@State<KpiDenominatorStateModel>({
    name: 'kpiDenominator',
    defaults: {
        organizationId: undefined,
        types: [],
        typesLoaded: false,
        denominators: [],
        loading: false,
        error: undefined,
    },
})
@Injectable()
export class KpiDenominatorState {
    constructor(private readonly kpiDenominatorService: KpiDenominatorHttpService) {}

    @Action(KpiDenominatorActions.LoadTypes)
    loadTypes(
        ctx: StateContext<KpiDenominatorStateModel>,
        action: KpiDenominatorActions.LoadTypes,
    ): Observable<OrganizationKpiDenominatorTypeDto[]> {
        // Switching organization: drop the previous organization's data immediately so
        // selectors never serve another tenant's KPIs while the reload is in flight.
        if (ctx.getState().organizationId !== action.orgId) {
            ctx.patchState({ types: [], denominators: [], typesLoaded: false });
        }
        ctx.patchState({ loading: true, organizationId: action.orgId });
        return this.kpiDenominatorService.getTypes(action.orgId).pipe(
            tap((types) =>
                ctx.patchState({ types: sortTypes(types), typesLoaded: true, loading: false, error: undefined }),
            ),
            catchError((err: Error) => {
                ctx.patchState({ loading: false, typesLoaded: true, error: err.message });
                return EMPTY;
            }),
        );
    }

    @Action(KpiDenominatorActions.CreateType)
    createType(
        ctx: StateContext<KpiDenominatorStateModel>,
        action: KpiDenominatorActions.CreateType,
    ): Observable<OrganizationKpiDenominatorTypeDto> {
        ctx.patchState({ loading: true });
        return this.kpiDenominatorService.createType(action.orgId, action.kpiType).pipe(
            tap((created) => {
                ctx.patchState({
                    types: sortTypes([...ctx.getState().types, created]),
                    loading: false,
                    error: undefined,
                });
                action.completion?.next(created);
            }),
            catchError((err: Error) => {
                ctx.patchState({ loading: false, error: err.message });
                action.completion?.next(undefined);
                return EMPTY;
            }),
        );
    }

    @Action(KpiDenominatorActions.UpdateType)
    updateType(
        ctx: StateContext<KpiDenominatorStateModel>,
        action: KpiDenominatorActions.UpdateType,
    ): Observable<OrganizationKpiDenominatorTypeDto> {
        ctx.patchState({ loading: true });
        return this.kpiDenominatorService.updateType(action.orgId, action.typeId, action.kpiType).pipe(
            tap((updated) => {
                ctx.patchState({
                    types: sortTypes(ctx.getState().types.map((t) => (t.id === action.typeId ? updated : t))),
                    loading: false,
                    error: undefined,
                });
                action.completion?.next(updated);
            }),
            catchError((err: Error) => {
                ctx.patchState({ loading: false, error: err.message });
                action.completion?.next(undefined);
                return EMPTY;
            }),
        );
    }

    @Action(KpiDenominatorActions.DeleteType)
    deleteType(
        ctx: StateContext<KpiDenominatorStateModel>,
        action: KpiDenominatorActions.DeleteType,
    ): Observable<void> {
        ctx.patchState({ loading: true });
        return this.kpiDenominatorService.deleteType(action.orgId, action.typeId).pipe(
            tap(() => {
                const state = ctx.getState();
                ctx.patchState({
                    types: state.types.filter((t) => t.id !== action.typeId),
                    // Mirror the DB's ON DELETE CASCADE so KPI cards recompute without a refetch.
                    denominators: state.denominators.filter((d) => d.kpiDenominatorTypeId !== action.typeId),
                    loading: false,
                    error: undefined,
                });
                action.completion?.next(true);
            }),
            catchError((err: Error) => {
                ctx.patchState({ loading: false, error: err.message });
                action.completion?.next(false);
                return EMPTY;
            }),
        );
    }

    @Action(KpiDenominatorActions.LoadDenominators)
    loadDenominators(
        ctx: StateContext<KpiDenominatorStateModel>,
        action: KpiDenominatorActions.LoadDenominators,
    ): Observable<EntityKpiDenominatorDto[]> {
        if (ctx.getState().organizationId !== action.orgId) {
            ctx.patchState({ types: [], denominators: [], typesLoaded: false });
        }
        ctx.patchState({ loading: true, organizationId: action.orgId });
        return this.kpiDenominatorService.getDenominators(action.orgId, action.years).pipe(
            tap((denominators) => ctx.patchState({ denominators, loading: false, error: undefined })),
            catchError((err: Error) => {
                ctx.patchState({ loading: false, error: err.message });
                return EMPTY;
            }),
        );
    }

    @Action(KpiDenominatorActions.UpsertDenominators)
    upsertDenominators(
        ctx: StateContext<KpiDenominatorStateModel>,
        action: KpiDenominatorActions.UpsertDenominators,
    ): Observable<void> {
        if (action.values.length === 0) {
            action.completion?.next(true);
            return EMPTY;
        }
        ctx.patchState({ loading: true });
        return this.kpiDenominatorService.upsertDenominators(action.orgId, { values: action.values }).pipe(
            tap(() => {
                // Merge the accepted values into the cache by composite key (entity, year, type).
                const merged = new Map(ctx.getState().denominators.map((d) => [denominatorKey(d), d]));
                action.values.forEach((v) => merged.set(denominatorKey(v), v));
                ctx.patchState({ denominators: [...merged.values()], loading: false, error: undefined });
                action.completion?.next(true);
            }),
            catchError((err: Error) => {
                ctx.patchState({ loading: false, error: err.message });
                action.completion?.next(false);
                return EMPTY;
            }),
        );
    }

    @Action(KpiDenominatorActions.ClearState)
    clearState(ctx: StateContext<KpiDenominatorStateModel>): void {
        ctx.setState({
            organizationId: undefined,
            types: [],
            typesLoaded: false,
            denominators: [],
            loading: false,
            error: undefined,
        });
    }

    // --- Selectors ---

    @Selector([KpiDenominatorState])
    static getTypes(state: KpiDenominatorStateModel): OrganizationKpiDenominatorTypeDto[] {
        return state.types;
    }

    @Selector([KpiDenominatorState])
    static isTypesLoaded(state: KpiDenominatorStateModel): boolean {
        return state.typesLoaded;
    }

    @Selector([KpiDenominatorState])
    static getDenominators(state: KpiDenominatorStateModel): EntityKpiDenominatorDto[] {
        return state.denominators;
    }

    @Selector([KpiDenominatorState])
    static isLoading(state: KpiDenominatorStateModel): boolean {
        return state.loading;
    }
}
